import { createContext, useContext } from "react";
import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from "../../../tailwind.onsite.config";
import chroma from "chroma-js";
import type { VenueBrand, BrandStyle } from "./schema";

const { theme } = resolveConfig(tailwindConfig);

interface VenueCssVariables {
  [index: `--venue-${string}`]: string;
}

export interface Brand extends VenueBrand {
  backgroundColor: chroma.Color;
  foregroundColor: chroma.Color;
  brandContrastColor: "white" | "black";
  isBrutalist: boolean;
  isSimple: boolean;
  isElegant: boolean;
  isPlayful: boolean;
  cssVariables: VenueCssVariables;
}
const findContrastColor = (brandColor: string) => {
  const contrastWith = (color: string) => chroma.contrast(brandColor, color);
  return contrastWith("white") > contrastWith("black") ? "white" : "black";
};

type CssVariableStyleOptions = {
  [index in BrandStyle]: string | undefined;
};
const radiuses: CssVariableStyleOptions = {
  brutalist: theme?.borderRadius?.none,
  simple: theme?.borderRadius?.md,
  elegant: theme?.borderRadius?.none,
  playful: theme?.borderRadius?.["xl"],
};

export const brandForContext = (venueBrand: VenueBrand): Brand => {
  const shadows: CssVariableStyleOptions = {
    brutalist: `4px 4px 0px 0 ${venueBrand.brandColor}`,
    simple: undefined,
    elegant: undefined,
    playful: undefined,
  };
  const colorScale = chroma
    .bezier([venueBrand.brandColor || "white", "white"])
    .scale()
    .correctLightness();
  const brandContrastColor = findContrastColor(
    venueBrand.brandColor || "white"
  );
  const foregroundColor = colorScale(0.95);
  const backgroundColor = colorScale(0.98);
  return {
    backgroundColor,
    foregroundColor,
    ...venueBrand,
    brandContrastColor,
    isBrutalist: venueBrand.brandStyle == "brutalist",
    isSimple: venueBrand.brandStyle == "simple" || !venueBrand.brandStyle,
    isElegant: venueBrand.brandStyle == "elegant",
    isPlayful: venueBrand.brandStyle == "playful",
    cssVariables: {
      "--venue-primary": venueBrand.brandColor,
      "--venue-secondary": venueBrand.secondaryColor,
      "--venue-tertiary": venueBrand.tertiaryColor,
      "--venue-highlight": venueBrand.highlightColor,
      "--venue-border-radius": radiuses[venueBrand.brandStyle] || "0",
      "--venue-box-shadow": shadows[venueBrand.brandStyle] || "none",
    },
  };
};

export const BrandContext = createContext<Brand | null>(null);
export const useBrand = () => {
  const brand = useContext(BrandContext);
  if (!brand) {
    throw new Error("useBrand must be used within a BrandContext");
  }
  return brand;
};
